import { useRef, useCallback, useEffect } from 'react';
import notificationNewSrc from '@/assets/sounds/notification-new.mp3';
import notificationChangeSrc from '@/assets/sounds/notification-change.mp3';
import notificationConnectedSrc from '@/assets/sounds/notification-connected.mp3';
import notificationMessageSrc from '@/assets/sounds/notification-message.mp3';
import { showErrorToast } from '@/utils/toasts';

export const SOUND_BLOCKED_MESSAGE =
  'Tonwiedergabe wurde vom Browser blockiert. Bitte Audio für diese Seite erlauben.';

export const showSoundBlockedToast = () => showErrorToast(SOUND_BLOCKED_MESSAGE);

type SoundType = 'new' | 'change' | 'connected' | 'message';

const SOUND_SOURCES: Record<SoundType, string> = {
  new: notificationNewSrc,
  change: notificationChangeSrc,
  connected: notificationConnectedSrc,
  message: notificationMessageSrc,
};

export function useNotificationSound(isMuted: boolean) {
  const audioRefs = useRef<Map<SoundType, HTMLAudioElement>>(new Map());

  const getAudio = (type: SoundType): HTMLAudioElement => {
    let audio = audioRefs.current.get(type);
    if (audio === undefined) {
      audio = new Audio(SOUND_SOURCES[type]);
      audioRefs.current.set(type, audio);
    }
    return audio;
  };

  const play = useCallback(async (type: SoundType, options?: { force?: boolean }): Promise<boolean> => {
    if (isMuted && options?.force !== true) return true;

    const audio = getAudio(type);
    audio.currentTime = 0;
    try {
      await audio.play();
      return true;
    } catch {
      return false;
    }
  }, [isMuted]);

  useEffect(() => {
    return () => {
      audioRefs.current.forEach((audio) => {
        audio.pause();
        audio.src = '';
      });
      audioRefs.current.clear();
    };
  }, []);

  return { play };
}
